import { Fragment, useCallback, useEffect, useMemo } from 'react';
import glassTinting from '~/assets/icons/glass_tinting.svg';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import {
	setActiveIndex,
	setActiveIndexNext,
	setActiveIndexPrev,
	setActiveStage,
	setMaxIndex,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Categories } from '../Categories';
import { MenuItem } from '../MenuItem';
import {
	StyledBottomContainer,
	StyledControlsContainer,
} from './Tinting.styled';

const Tinting = () => {
	const dispatch = useLayoutDispatch();
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const windowTints = useObservable(
		rage.vehiclePaintShop.getVehicleWindowTints(),
	);
	const ownedVehiclePaint = useLayoutSelector(
		(state) => state.carPaint.ownedVehiclePaint,
	);
	const { handleBuy } = useBuy();

	const items = useMemo(() => {
		return windowTints.map((item) => ({
			name: item.name,
			price: item.price,
			value: item.value,
		}));
	}, [windowTints]);

	const handleBack = useCallback(() => {
		dispatch(setActiveIndex(1));
		dispatch(setActiveStage(TuningStudioStage.MAIN));
	}, []);

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState || interfaceHidden) return;
		dispatch(setActiveIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState || interfaceHidden) return;
		dispatch(setActiveIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState || interfaceHidden) return;
		handleBuy(WalletType.Cash);
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState || interfaceHidden) return;
		handleBack();
	});

	useEffect(() => {
		if (!items[activeIndex]) {
			return;
		}

		rage.vehiclePaintShop
			.previewWindowTint(items[activeIndex].value)
			.catch((err) => rage.notifications.showException(err));
	}, [activeIndex, items, ownedVehiclePaint?.windowTint]);

	useEffect(() => {
		if (items.length === 0) {
			dispatch(setMaxIndex(0));
			return;
		}

		dispatch(setActiveIndex(0));
		dispatch(setMaxIndex(items.length - 1));
	}, [items.length]);

	const leftControls = useMemo(
		() => [
			{
				text: 'Управление',
				keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
			},
			{
				text: 'Купить',
				keys: [InputKey.Enter],
				onClick: () => handleBuy(WalletType.Cash),
			},
			{
				text: 'Крутить камеру',
				keys: [InputKey.MouseRight],
			},
		],
		[handleBuy],
	);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => handleBack(),
		},
	];

	return (
		<Fragment>
			<StyledBottomContainer>
				<Categories
					name="Тонировка"
					activeIndex={activeIndex}
					totalCount={items.length}
					handleBack={handleBack}
					handlePrev={() => dispatch(setActiveIndexPrev())}
					handleNext={() => dispatch(setActiveIndexNext())}
				>
					{items.map((item, index) => {
						return (
							<MenuItem
								key={index}
								type="item"
								name={item.name}
								icon={glassTinting}
								active={activeIndex === index}
								onClick={() => dispatch(setActiveIndex(index))}
								price={item.price}
								id={`tintItem#${index}`}
							/>
						);
					})}
				</Categories>
			</StyledBottomContainer>
			<StyledControlsContainer style={{ opacity: interfaceHidden ? 0 : 1 }}>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { Tinting };
